import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Instagram, Clock, ArrowUpRight, ChevronDown, Facebook, MessageCircle } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import { SITE, NAV } from "@/data/site";

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative overflow-hidden bg-forest text-ivory pt-24 pb-10"
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-raspberry/25 blur-3xl"
            />
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-sage/25 blur-3xl"
            />
            <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-14 lg:gap-16 pb-16 border-b border-ivory/15">
                    <div className="lg:col-span-5">
                        <SectionLabel number="01" text="Spazio Isa" tone="light" />
                        <h3 className="mt-6 font-display text-[34px] sm:text-[44px] leading-[1.05] tracking-tight">
                            Un rituale <em className="text-rose-powder not-italic">di bellezza</em>,
                            <br /> tutto per te.
                        </h3>
                        <p className="mt-6 max-w-md text-ivory/75 text-[15px] leading-relaxed">
                            Da oltre 15 anni un punto di riferimento nel
                            quartiere Coteto per la cura del viso, del corpo e
                            delle mani.
                        </p>
                        <Link
                            to="/contatti"
                            data-testid="footer-cta"
                            className="mt-8 inline-flex items-center gap-3 rounded-full bg-ivory text-forest pl-6 pr-2 py-2 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-rose-powder transition-colors"
                        >
                            Prenota il tuo momento
                            <span className="w-9 h-9 rounded-full bg-raspberry text-ivory grid place-items-center">
                                <ArrowUpRight size={16} />
                            </span>
                        </Link>
                    </div>

                    <div className="lg:col-span-3">
                        <SectionLabel text="Navigazione" tone="light" />
                        <ul className="mt-6 space-y-3.5 font-display text-xl">
                            {NAV.map((n) =>
                                n.children ? (
                                    <li key={n.label}>
                                        <FooterServiziMenu item={n} />
                                    </li>
                                ) : (
                                    <li key={n.to}>
                                        <Link
                                            to={n.to}
                                            className="link-underline hover:text-rose-powder transition-colors"
                                        >
                                            {n.label}
                                        </Link>
                                    </li>
                                ),
                            )}
                        </ul>
                    </div>

                    <div className="lg:col-span-4 space-y-8">
                        <div>
                            <SectionLabel text="Contatti" tone="light" />
                            <ul className="mt-5 space-y-4 text-[15px]">
                                <li className="flex items-start gap-3">
                                    <MapPin
                                        size={18}
                                        className="mt-0.5 text-rose-powder shrink-0"
                                    />
                                    <span className="text-ivory/85 leading-relaxed">
                                        {SITE.address.street}
                                        <br />
                                        {SITE.address.cap} {SITE.address.city}{" "}
                                        · {SITE.address.district}
                                    </span>
                                </li>
                                <li className="flex items-center gap-3">
                                    <Phone
                                        size={18}
                                        className="text-rose-powder shrink-0"
                                    />
                                    <a
                                        href={SITE.phoneHref}
                                        className="text-ivory/85 hover:text-rose-powder"
                                    >
                                        {SITE.phone}
                                    </a>
                                    <span className="text-ivory/40">·</span>
                                    <a
                                        href={SITE.whatsappHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-ivory/85 hover:text-rose-powder"
                                    >
                                        {SITE.mobile}
                                    </a>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Clock
                                        size={18}
                                        className="mt-0.5 text-rose-powder shrink-0"
                                    />
                                    <div className="text-ivory/85 leading-relaxed">
                                        Mar – Ven · 09:00 – 18:30
                                        <br />
                                        Sabato · 09:00 – 13:00
                                    </div>
                                </li>
                                <li className="flex items-center gap-4 pt-2">
                                    <a
                                        href={SITE.facebookUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="footer-facebook"
                                        aria-label="Facebook"
                                        className="w-10 h-10 rounded-full bg-ivory/10 hover:bg-rose-powder hover:text-forest text-ivory grid place-items-center transition-colors"
                                    >
                                        <Facebook size={16} />
                                    </a>
                                    <a
                                        href={SITE.instagramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="footer-instagram"
                                        aria-label="Instagram"
                                        className="w-10 h-10 rounded-full bg-ivory/10 hover:bg-rose-powder hover:text-forest text-ivory grid place-items-center transition-colors"
                                    >
                                        <Instagram size={16} />
                                    </a>
                                    <a
                                        href={SITE.whatsappHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="footer-whatsapp"
                                        aria-label="WhatsApp"
                                        className="w-10 h-10 rounded-full bg-ivory/10 hover:bg-rose-powder hover:text-forest text-ivory grid place-items-center transition-colors"
                                    >
                                        <MessageCircle size={16} />
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 text-[12px] text-ivory/60 tracking-wide">
                    <p>
                        © {new Date().getFullYear()} Spazio Isa · Centro
                        Estetico Livorno · P.IVA a richiesta
                    </p>
                    <div className="flex items-center gap-6">
                        <Link
                            to="/cookie"
                            data-testid="footer-cookie"
                            className="link-underline hover:text-rose-powder"
                        >
                            Cookie Policy
                        </Link>
                        <Link
                            to="/privacy"
                            data-testid="footer-privacy"
                            className="link-underline hover:text-rose-powder"
                        >
                            Privacy Policy
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FooterServiziMenu({ item }) {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="footer-servizi-toggle"
                aria-expanded={open}
                className="flex items-center gap-2 link-underline hover:text-rose-powder transition-colors"
            >
                {item.label}
                <ChevronDown
                    size={16}
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <ul
                    data-testid="footer-servizi-submenu"
                    className="mt-3 ml-4 space-y-2.5 font-display text-lg text-ivory/80 border-l border-ivory/20 pl-4"
                >
                    {item.children.map((c) => (
                        <li key={c.to}>
                            <Link
                                to={c.to}
                                data-testid={`footer-servizi-${c.label
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}`}
                                className="hover:text-rose-powder transition-colors"
                            >
                                {c.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
